// Otimização de escaneados para os processos: só para arquivo COMUM acima de 3 MB (assinado nunca passa aqui).
// Tudo no navegador: o arquivo grande nem chega ao servidor.
//
// Cada página é redesenhada em resolução de leitura, em tons de cinza, e comprimida em JPEG; depois vira um PDF
// só (pdfDeImagens.js). Tenta do nível mais leve ao mais forte; se nem o mais forte couber em 3 MB, divide em
// partes que caibam, mantendo a ordem das páginas.
import { montarPdfDeJpegs, dividirEmPartes, tamanhoEstimado } from './pdfDeImagens';
import { LIMITE_COMUM_MB, MB, nomeOtimizado } from './processos';

const BASE_PDFJS = `${window.location.origin}${process.env.PUBLIC_URL || ''}/ocr`;
const LIMITE_BYTES = LIMITE_COMUM_MB * MB;
const LADO_MAXIMO_PX = 2600; // segurança contra página gigante (planta, mapa) virar um canvas enorme

export const NIVEIS = [
  { nome: 'leve', dpi: 150, qualidade: 0.72 },
  { nome: 'médio', dpi: 120, qualidade: 0.58 },
  { nome: 'forte', dpi: 96, qualidade: 0.45 },
];

// Lê o arquivo uma vez e devolve uma função que desenha cada página na resolução pedida
async function abrirPaginas(arquivo) {
  if (arquivo.type === 'application/pdf') {
    const pdfjs = await import('pdfjs-dist/es5/build/pdf');
    pdfjs.GlobalWorkerOptions.workerSrc = `${BASE_PDFJS}/pdf.worker.min.js`;
    const pdf = await pdfjs.getDocument({ data: await arquivo.arrayBuffer() }).promise;
    return {
      total: pdf.numPages,
      async desenhar(indice, dpi) {
        const pagina = await pdf.getPage(indice + 1);
        const base = pagina.getViewport({ scale: 1 }); // 1 = 72 dpi (pontos)
        const escala = Math.min(dpi / 72, LADO_MAXIMO_PX / Math.max(base.width, base.height));
        const viewport = pagina.getViewport({ scale: escala });
        const canvas = criarCanvas(viewport.width, viewport.height);
        await pagina.render({ canvasContext: canvas.getContext('2d'), viewport }).promise;
        return { canvas, larguraPt: base.width, alturaPt: base.height };
      },
    };
  }
  // Foto (JPG/PNG): uma página, no tamanho A4 proporcional
  const imagem = await carregarImagem(arquivo);
  return {
    total: 1,
    async desenhar(_indice, dpi) {
      const larguraPt = 595.28;
      const alturaPt = larguraPt * (imagem.height / imagem.width);
      const escala = Math.min(1, (larguraPt * dpi / 72) / imagem.width, LADO_MAXIMO_PX / Math.max(imagem.width, imagem.height));
      const canvas = criarCanvas(imagem.width * escala, imagem.height * escala);
      canvas.getContext('2d').drawImage(imagem, 0, 0, canvas.width, canvas.height);
      return { canvas, larguraPt, alturaPt };
    },
  };
}

// onProgresso(percentual, mensagem)
// Devolve { partes: [{ arquivo: File, tamanho, paginas }], nivel, tamanhoOriginal, previaUrl } ou lança erro.
export async function otimizarArquivo(arquivo, onProgresso = () => {}) {
  onProgresso(2, 'Abrindo o documento...');
  const paginas = await abrirPaginas(arquivo);
  const etapas = NIVEIS.length * paginas.total;
  let feitas = 0;

  let ultimo = null;
  for (const nivel of NIVEIS) {
    const jpegs = [];
    for (let i = 0; i < paginas.total; i++) {
      onProgresso(5 + Math.round((feitas / etapas) * 90), `Otimizando (${nivel.nome}) — página ${i + 1} de ${paginas.total}...`);
      const { canvas, larguraPt, alturaPt } = await paginas.desenhar(i, nivel.dpi);
      paraCinza(canvas);
      const jpeg = await canvasParaJpeg(canvas, nivel.qualidade);
      jpegs.push({ jpeg, larguraPx: canvas.width, alturaPx: canvas.height, larguraPt, alturaPt });
      liberar(canvas);
      feitas++;
    }
    ultimo = { nivel, jpegs };
    if (tamanhoEstimado(jpegs.map((p) => p.jpeg.length)) <= LIMITE_BYTES) break;
  }

  const { nivel, jpegs } = ultimo;
  const grupos = dividirEmPartes(jpegs.map((p) => p.jpeg.length), LIMITE_BYTES);
  if (!grupos) {
    throw new Error('Uma das páginas continua acima de 3 MB mesmo otimizada. Escaneie essa página com resolução menor.');
  }
  const partes = grupos.map((indices, n) => {
    const bytes = montarPdfDeJpegs(indices.map((i) => jpegs[i]));
    const nome = nomeOtimizado(arquivo.name, n + 1, grupos.length);
    return { arquivo: new File([bytes], nome, { type: 'application/pdf' }), tamanho: bytes.length, paginas: indices.length };
  });
  const previaUrl = URL.createObjectURL(new Blob([jpegs[0].jpeg], { type: 'image/jpeg' }));
  onProgresso(100, 'Pronto');
  return { partes, nivel: nivel.nome, tamanhoOriginal: arquivo.size, totalPaginas: paginas.total, previaUrl };
}

function criarCanvas(largura, altura) {
  const canvas = document.createElement('canvas');
  canvas.width = Math.max(1, Math.round(largura));
  canvas.height = Math.max(1, Math.round(altura));
  return canvas;
}

// Tons de cinza: documento escaneado não precisa de cor, e o JPEG fica bem menor
function paraCinza(canvas) {
  const ctx = canvas.getContext('2d');
  const imagem = ctx.getImageData(0, 0, canvas.width, canvas.height);
  const d = imagem.data;
  for (let i = 0; i < d.length; i += 4) {
    const y = (d[i] * 299 + d[i + 1] * 587 + d[i + 2] * 114) / 1000;
    d[i] = d[i + 1] = d[i + 2] = y;
  }
  ctx.putImageData(imagem, 0, 0);
}

function canvasParaJpeg(canvas, qualidade) {
  return new Promise((resolve, reject) => {
    canvas.toBlob(async (blob) => {
      if (!blob) { reject(new Error('Não foi possível gerar a imagem da página')); return; }
      resolve(new Uint8Array(await blob.arrayBuffer()));
    }, 'image/jpeg', qualidade);
  });
}

// Solta a memória do canvas na hora (documentos com muitas páginas)
function liberar(canvas) {
  canvas.width = 0;
  canvas.height = 0;
}

function carregarImagem(arquivo) {
  return new Promise((resolve, reject) => {
    const url = URL.createObjectURL(arquivo);
    const imagem = new Image();
    imagem.onload = () => { URL.revokeObjectURL(url); resolve(imagem); };
    imagem.onerror = () => { URL.revokeObjectURL(url); reject(new Error('Não foi possível abrir a imagem')); };
    imagem.src = url;
  });
}
